import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Leaf, Info } from 'lucide-react';
import { useMemo } from 'react';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useEmissionFactorTableIndex } from '@/hooks/useEmissionFactorTableIndex';
import {
  useCarbonTableColumns,
  useCarbonColumnValues,
  useCompatibleMultiplierUnits,
  useFactorDimensionRows,
} from '@/hooks/useCarbonConfig';
import {
  computeDimensionCascade,
  DIMENSION_NONE_VALUE,
  tableEverUsesNoneValue,
  withNoneOption,
} from '@/lib/dimensionCascade';
import { unitLabel } from '@releve/shared/units';

/**
 * Table codes for which a sensible default pin is auto-applied the first time
 * the operator selects the table. Each entry maps to the JSONB `dimension_filter`
 * that should be seeded. Keep this list small and intentional — these are
 * tables whose default lookup is otherwise ambiguous (multiple rows per
 * user-pickable dimension combination).
 */
const DEFAULT_DIMENSION_FILTERS: Record<string, Record<string, string>> = {
  nl_co2emissiefactoren_waste: { component: 'ghg_total' },
};

interface AccountCarbonSettingsProps {
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  tableId?: string;
  onTableIdChange: (tableId: string) => void;
  multiplierCode?: string;
  onMultiplierCodeChange: (code: string) => void;
  multiplierLabel?: string;
  onMultiplierLabelChange: (label: string) => void;
  multiplierUnit?: string;
  onMultiplierUnitChange: (unit: string) => void;
  dimensionFilter: Record<string, string>;
  onDimensionFilterChange: (filter: Record<string, string>) => void;
  multiplierUnitCode: string | null;
  onMultiplierUnitCodeChange: (code: string | null) => void;
}

const LEAVE_OPEN_VALUE = '__leave_open__';

export function AccountCarbonSettings({
  enabled,
  onEnabledChange,
  tableId,
  onTableIdChange,
  multiplierCode,
  onMultiplierCodeChange,
  multiplierLabel,
  onMultiplierLabelChange,
  multiplierUnit,
  onMultiplierUnitChange,
  dimensionFilter,
  onDimensionFilterChange,
  multiplierUnitCode,
  onMultiplierUnitCodeChange,
}: AccountCarbonSettingsProps) {
  // Available emission-factor tables, off the shared org-scoped index.
  //
  // FIX, not just a swap: the read this replaced had NO org filter and NO org
  // in its cache key (`['emission-factor-tables-for-accounts']`). RLS still
  // scoped the rows, so a user who belongs to several orgs saw the UNION of
  // every org's tables — and, because the key had no org slot, whichever org's
  // union loaded first stayed cached across an org switch. The index endpoint
  // scopes to globals + THIS org and the key carries the org id.
  //
  // Inactive tables are filtered here (the index deliberately returns both) —
  // pinning a soft-deleted table to an account would resolve to no factor.
  const { selectedOrganization } = useOrganization();
  const { data: allTables } = useEmissionFactorTableIndex(selectedOrganization?.id);
  const tables = useMemo(
    () => (allTables ?? []).filter((tbl) => tbl.is_active),
    [allTables],
  );

  // Columns + column values for the selected table — used both for the
  // "inputs preview" badge block and for the new per-column pin pickers.
  const { data: columns } = useCarbonTableColumns(tableId);
  const { data: columnValues } = useCarbonColumnValues(columns);

  // Distinct dimension combinations that actually exist as factors, so the pin
  // dropdowns only offer values that can resolve to an emission factor. Feeding
  // the current pins as the "selection" narrows each dropdown by the pins to its
  // left (columns render alphabetically by display_label, same order the cascade
  // walks) — so an operator can't pin a combination with no matching factor.
  const { data: factorDimRows } = useFactorDimensionRows(tableId);
  const { optionsByCode } = useMemo(
    () => computeDimensionCascade(columns, factorDimRows, dimensionFilter ?? {}),
    [columns, factorDimRows, dimensionFilter],
  );

  // Multiplier-unit dropdown options derived from the factor's own input unit.
  // `factorInputUnitCode` is null when the factor table has no input_unit_code
  // configured yet (custom org factor) — in that case we fall back to the
  // legacy freeform multiplier fields below.
  const { units: compatibleUnits, factorInputUnitCode } =
    useCompatibleMultiplierUnits(tableId);

  const selectedTable = tables.find((t) => t.id === tableId);

  // Picking a unit_code auto-derives the legacy display fields so existing
  // rows and queries keep working during rollout. Nothing reads the legacy
  // fields from state — they're persisted solely for back-compat until the
  // Phase F chore migration drops them.
  const handleMultiplierUnitCodeChange = (code: string) => {
    onMultiplierUnitCodeChange(code || null);
    if (code) {
      onMultiplierCodeChange(code);
      onMultiplierLabelChange(unitLabel(code));
      onMultiplierUnitChange(code);
    }
  };

  // When the operator picks a table that has a known-good default pin and
  // they haven't pinned anything yet, seed the default. Explicitly guarded
  // so we never clobber a pin the operator has set.
  const handleTableIdChange = (newTableId: string) => {
    onTableIdChange(newTableId);
    const nextTable = tables.find((t) => t.id === newTableId);
    if (!nextTable) return;
    const defaults = DEFAULT_DIMENSION_FILTERS[nextTable.table_code];
    const isCurrentlyEmpty = Object.keys(dimensionFilter ?? {}).length === 0;
    if (defaults && isCurrentlyEmpty) {
      onDimensionFilterChange(defaults);
    }
  };

  const handlePinChange = (columnCode: string, valueCode: string) => {
    const next = { ...(dimensionFilter ?? {}) };
    if (valueCode === LEAVE_OPEN_VALUE || valueCode === '') {
      delete next[columnCode];
    } else {
      next[columnCode] = valueCode;
    }
    onDimensionFilterChange(next);
  };

  const valuesForColumn = (columnId: string) => {
    const realValues = columnValues?.filter((v) => v.column_id === columnId) ?? [];
    const column = columns?.find((c) => c.id === columnId);
    // Optional select columns also offer an explicit "(None)" pin — see
    // DIMENSION_NONE_VALUE — but ONLY when some factor row in the table
    // actually uses it. Unlike EmissionFactorDialog (where an admin is
    // WRITING a new factor row and needs to be able to create that state for
    // the first time), this pin selector matches AGAINST existing data: if no
    // row anywhere in the table has DIMENSION_NONE_VALUE for this column,
    // offering it here would let an admin pin the account to a value
    // lookup_emission_factor can never match, silently breaking carbon
    // resolution for every transaction with no error surfaced anywhere.
    if (!tableEverUsesNoneValue(column?.column_code ?? '', factorDimRows)) return realValues;
    return withNoneOption(
      realValues,
      column?.is_required,
      () => ({
        id: DIMENSION_NONE_VALUE,
        column_id: columnId,
        value_code: DIMENSION_NONE_VALUE,
        display_label: '(None)',
      }),
      (v) => v.value_code,
    );
  };

  const selectColumns = columns?.filter((c) => c.column_type === 'select') ?? [];
  const pinCount = Object.keys(dimensionFilter ?? {}).length;

  return (
    <Card className="border-emerald-200 bg-emerald-50/30">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Leaf className="h-5 w-5 text-emerald-600" />
            <CardTitle>Carbon Tracking</CardTitle>
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={onEnabledChange}
          />
        </div>
        <CardDescription>
          Track carbon emissions for transactions using this account
        </CardDescription>
      </CardHeader>

      {enabled && (
        <CardContent className="space-y-4">
          {/* Select Emission Factor Table */}
          <div className="space-y-2">
            <Label htmlFor="emission-table">Emission Factor Table</Label>
            <Select value={tableId || ''} onValueChange={handleTableIdChange}>
              <SelectTrigger id="emission-table">
                <SelectValue placeholder="Select an emission factor table..." />
              </SelectTrigger>
              <SelectContent>
                {tables.map((table) => (
                  <SelectItem key={table.id} value={table.id}>
                    <div className="flex flex-col max-w-prose">
                      <span>{table.table_name}</span>
                      {table.description && (
                        <span className="text-xs text-muted-foreground whitespace-normal break-words">
                          {table.description}
                        </span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {tableId && columns && columns.length > 0 && (
              <div className="flex items-start gap-2 p-2 bg-blue-50 border border-blue-200 rounded-md text-xs">
                <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-blue-900 mb-1">Transaction inputs will include:</p>
                  <div className="flex flex-wrap gap-1">
                    {columns
                      .filter((col) => !(col.column_code in (dimensionFilter ?? {})))
                      .map((col) => (
                        <Badge key={col.id} variant="outline" className="text-xs">
                          {col.display_label}
                        </Badge>
                      ))}
                    <Badge variant="outline" className="text-xs bg-emerald-100">
                      + Multiplier (below)
                    </Badge>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Pin Dimensions */}
          {tableId && selectColumns.length > 0 && (
            <div className="border-t pt-4 space-y-3">
              <div>
                <Label className="text-sm font-medium">Pin dimensions at this account</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Fix any dimension to a specific value so it doesn't appear on every
                  transaction. Leave a dimension open to let the user pick it per
                  transaction. Pinning is required when a table has multiple rows per
                  user-picked combination (e.g., the NL waste table's <code>component</code>).
                </p>
                {pinCount > 0 && (
                  <p className="text-xs text-emerald-700 mt-1">
                    {pinCount} dimension{pinCount === 1 ? '' : 's'} pinned
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {selectColumns.map((column) => {
                  const currentValue = dimensionFilter?.[column.column_code] ?? '';
                  // Restrict options to values that occur in real factor rows
                  // (given the other pins). Keep the currently-pinned value even
                  // if it falls outside the narrowed set so it stays visible/
                  // changeable; if the cascade has no info for this column yet
                  // (still loading, or a column no factor uses) show everything.
                  const valid = optionsByCode[column.column_code];
                  const options = valuesForColumn(column.id).filter(
                    (v) =>
                      !valid ||
                      valid.size === 0 ||
                      valid.has(v.value_code) ||
                      v.value_code === currentValue,
                  );
                  return (
                    <div key={column.id} className="space-y-1">
                      <Label htmlFor={`pin-${column.column_code}`} className="text-xs">
                        {column.display_label}
                      </Label>
                      <Select
                        value={currentValue || LEAVE_OPEN_VALUE}
                        onValueChange={(value) => handlePinChange(column.column_code, value)}
                      >
                        <SelectTrigger id={`pin-${column.column_code}`} className="h-9 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={LEAVE_OPEN_VALUE} className="text-xs italic text-muted-foreground">
                            — leave open (user picks per transaction) —
                          </SelectItem>
                          {options.map((v) => (
                            <SelectItem key={v.id} value={v.value_code} className="text-xs">
                              {v.display_label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  );
                })}
              </div>

              {selectedTable?.table_code === 'nl_co2emissiefactoren_waste' &&
                dimensionFilter?.component === 'ghg_total' && (
                  <div className="flex items-start gap-2 p-2 bg-emerald-50 border border-emerald-200 rounded-md text-xs">
                    <Info className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <p className="text-emerald-900">
                      Using the GHG Protocol total (processing + transport) — the ready-to-use
                      value for Scope 3 reporting. Change to a detail component (processing,
                      transport, treatment, avoided) if you need the LCA breakdown instead.
                    </p>
                  </div>
                )}
            </div>
          )}

          {/* Multiplier Unit — typed dropdown filtered by compatibility with
              the factor's input_unit_code. The picked code auto-derives the
              legacy multiplier_* fields for back-compat during rollout. */}
          {tableId && factorInputUnitCode && (
            <div className="border-t pt-4 space-y-3">
              <div>
                <Label htmlFor="multiplier-unit-code" className="text-sm font-medium">
                  Multiplier Unit
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Factor expects input in{' '}
                  <strong>{unitLabel(factorInputUnitCode)} ({factorInputUnitCode})</strong>.
                  Pick the unit users will enter per transaction — we'll convert for you.
                </p>
              </div>

              <Select
                value={multiplierUnitCode ?? ''}
                onValueChange={handleMultiplierUnitCodeChange}
              >
                <SelectTrigger id="multiplier-unit-code" className="text-sm">
                  <SelectValue placeholder="Select a unit..." />
                </SelectTrigger>
                <SelectContent>
                  {compatibleUnits.map((u) => (
                    <SelectItem key={u.code} value={u.code} className="text-sm">
                      {u.label} <span className="text-muted-foreground">({u.code})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {multiplierUnitCode && multiplierUnitCode !== factorInputUnitCode && (
                <div className="flex items-start gap-2 p-2 bg-blue-50 border border-blue-200 rounded-md text-xs">
                  <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <p className="text-blue-900">
                    Per-transaction <strong>{unitLabel(multiplierUnitCode)}</strong> input will
                    be converted to <strong>{unitLabel(factorInputUnitCode)}</strong> before
                    multiplying by the factor.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Legacy freeform fallback — shown only when the factor table has
              no input_unit_code configured (custom org factor). The admin can
              set it via the EmissionFactorDialog in Settings. */}
          {tableId && !factorInputUnitCode && (
            <div className="border-t pt-4 space-y-3">
              <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded-md text-xs">
                <Info className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-amber-900">
                  This factor table has no canonical input unit yet — unit reconciliation is off.
                  Configure it in Settings &rarr; Emission Factors so users get automatic unit
                  conversion, or enter a freeform multiplier below.
                </p>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="multiplier-code" className="text-xs">Code (Optional)</Label>
                  <Input
                    id="multiplier-code"
                    placeholder="e.g., distance_km"
                    value={multiplierCode || ''}
                    onChange={(e) => onMultiplierCodeChange(e.target.value)}
                    className="text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="multiplier-label" className="text-xs">Display Label (Optional)</Label>
                  <Input
                    id="multiplier-label"
                    placeholder="e.g., Distance"
                    value={multiplierLabel || ''}
                    onChange={(e) => onMultiplierLabelChange(e.target.value)}
                    className="text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="multiplier-unit" className="text-xs">Unit (Optional)</Label>
                  <Input
                    id="multiplier-unit"
                    placeholder="e.g., kilometers"
                    value={multiplierUnit || ''}
                    onChange={(e) => onMultiplierUnitChange(e.target.value)}
                    className="text-sm"
                  />
                </div>
              </div>
            </div>
          )}

          {!tableId && (
            <div className="p-3 bg-muted rounded-md text-xs text-muted-foreground">
              Select an emission factor table to configure the multiplier dimension
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
